/**
 * UGC Studio — Single Source of Truth dla wszystkich kosztów USD i przelicznika
 * USD → app tokens. Importowane RÓWNOLEGLE przez backend (server/modules/ugc-studio)
 * i frontend (client/src/components/ugc-studio) — żadnego duplikowania stałych.
 *
 * Token economy:
 *   tokens = USD × USD_TO_PLN × MARGIN ÷ PLN_PER_TOKEN
 *
 * Margin 5× — koszt usera vs koszt OPS, pokrywa: marżę handlową, narzut
 * infrastruktury (R2, ffmpeg CPU, kolejka), oraz poślizg cenowy fal.ai/EL.
 */

// ── Token economy ───────────────────────────────────────────
export const USD_TO_PLN = 4.0;
export const PLN_PER_TOKEN = 0.015625;
export const MARGIN_MULTIPLIER = 5.0;
export const LLM_OVERHEAD_TOKENS = 240;

/** Konwersja USD → app tokens, z marżą. */
export function usdToTokens(usd: number): number {
  if (usd <= 0) return 0;
  return Math.ceil((usd * USD_TO_PLN * MARGIN_MULTIPLIER) / PLN_PER_TOKEN);
}

// ── fal.ai video pricing ────────────────────────────────────
/**
 * Seedance 2.0 reference-to-video. 720p = $0.3024/s baseline (verified
 * fal.ai/models/bytedance/seedance-2.0/reference-to-video). 480p i 1080p
 * pochodzą ze skalowania pixel-ratio (16:9).
 */
export const FAL_PRICING_USD = {
  seedancePerSecond: {
    "480p": 0.1345,
    "720p": 0.3024,
    "1080p": 0.6804,
  },
  /** fal-ai/sync-lipsync/v2 — ~$0.10 per second of output. */
  lipsyncPerSecond: 0.1,
} as const;

export type SeedanceResolution = keyof typeof FAL_PRICING_USD.seedancePerSecond;

export function seedanceUsd(resolution: SeedanceResolution, durationSeconds: number): number {
  return FAL_PRICING_USD.seedancePerSecond[resolution] * durationSeconds;
}

export function lipsyncUsd(durationSeconds: number): number {
  return FAL_PRICING_USD.lipsyncPerSecond * durationSeconds;
}

// ── ElevenLabs ──────────────────────────────────────────────
/**
 * eleven_multilingual_v2 — Creator+ tier ≈ $0.30 / 1k znaków.
 * Voice Design generuje 3 podglądy + zapis. Realny koszt ~$1 / sesja designu.
 */
export const ELEVENLABS_USD_PER_1K_CHARS = 0.3;
export const ELEVENLABS_VOICE_DESIGN_USD = 1.0;

export function elevenlabsTtsUsd(charCount: number): number {
  if (charCount <= 0) return 0;
  return (charCount / 1000) * ELEVENLABS_USD_PER_1K_CHARS;
}

// ── OpenAI ──────────────────────────────────────────────────
/**
 * Whisper-1 — $0.006 / minuta audio (1s = $0.0001).
 * Min charge: 1s (zaokrąglamy w górę).
 */
export const WHISPER_USD_PER_MINUTE = 0.006;
export const WHISPER_USD_PER_SECOND = WHISPER_USD_PER_MINUTE / 60;

export function whisperUsd(durationSeconds: number): number {
  if (durationSeconds <= 0) return 0;
  return Math.ceil(durationSeconds) * WHISPER_USD_PER_SECOND;
}

/**
 * gpt-image-2 (via fal-ai/gpt-image-2). Ceny per obrazek 1024×1024+
 * pochodzą z fal.ai pricing. Quality `auto` ≈ `medium` w praktyce.
 */
export const GPT_IMAGE_2_USD_BY_QUALITY = {
  low: 0.011,
  medium: 0.042,
  high: 0.167,
  auto: 0.042,
} as const;

export type GptImage2Quality = keyof typeof GPT_IMAGE_2_USD_BY_QUALITY;

/** Domyślna jakość avatara — balans między ceną a fotorealizmem. */
export const DEFAULT_AVATAR_QUALITY: GptImage2Quality = "medium";

export function gptImage2Usd(quality: GptImage2Quality = DEFAULT_AVATAR_QUALITY): number {
  return GPT_IMAGE_2_USD_BY_QUALITY[quality];
}

/**
 * gpt-4o-mini — $0.15 / 1M input + $0.60 / 1M output. Średni krótki call
 * (system + user + JSON response) ≈ 800 input + 300 output ≈ $0.0003.
 * Stosujemy flat estymatę dla preview-LLM (script generator + auto-script).
 */
export const GPT_4O_MINI_AVG_REQUEST_USD = 0.0003;

// ── Audio pipeline cost composer ────────────────────────────
export interface AudioPipelineCostInput {
  audioMode: "native" | "overlay" | "lipsync" | "voiceover";
  durationSeconds: number;
  /**
   * Liczba znaków scriptu (jeżeli user dał manualny). `null` lub `undefined`
   * = script zostanie wygenerowany automatycznie (LLM/Whisper) — wtedy do
   * estymaty bierzemy worst-case ≈ 3 znaki/s × duration.
   */
  scriptCharCount: number | null;
  /**
   * Czy pipeline zawiera transkrypcję Whisperem (multi-shot whisper:
   * audioMode≠native + brak manualnego scriptu + duration>15s).
   */
  whisperNeeded: boolean;
}

export interface AudioPipelineCostBreakdown {
  ttsUsd: number;
  lipsyncUsd: number;
  whisperUsd: number;
  scriptLlmUsd: number;
}

/** Per-job worst-case audio cost. Używane w `calculateTokenCost`. */
export function computeAudioPipelineUsd(input: AudioPipelineCostInput): AudioPipelineCostBreakdown {
  if (input.audioMode === "native") {
    return { ttsUsd: 0, lipsyncUsd: 0, whisperUsd: 0, scriptLlmUsd: 0 };
  }
  // Worst-case: gdy user nie dał scriptu, zakładamy ~3 znaki na sekundę audio.
  const charCount = input.scriptCharCount ?? Math.ceil(input.durationSeconds * 3);
  const ttsUsd = elevenlabsTtsUsd(charCount);
  const lipsyncUsdAmount = input.audioMode === "lipsync" ? lipsyncUsd(input.durationSeconds) : 0;
  const whisperUsdAmount = input.whisperNeeded ? whisperUsd(input.durationSeconds) : 0;
  // Auto-script (LLM) tylko gdy scriptCharCount === null AND !whisperNeeded.
  const needsLlmAutoScript = input.scriptCharCount == null && !input.whisperNeeded;
  const scriptLlmUsd = needsLlmAutoScript ? GPT_4O_MINI_AVG_REQUEST_USD : 0;
  return {
    ttsUsd,
    lipsyncUsd: lipsyncUsdAmount,
    whisperUsd: whisperUsdAmount,
    scriptLlmUsd,
  };
}

// ── Cost breakdown (persisted on job) ───────────────────────
/**
 * Składowe kosztu joba zapisywane w `ugcStudioJobs.costBreakdown`.
 * Wszystkie wartości w app tokens (po marży). Suma = `tokensCharged`.
 */
export interface UgcJobCostBreakdown {
  videoTokens: number;
  ttsTokens: number;
  lipsyncTokens: number;
  whisperTokens: number;
  scriptLlmTokens: number;
  llmOverheadTokens: number;
}

/** Etap, na którym aktualnie znajduje się job — używany do refundów etapowych. */
export type UgcJobStage = "queued" | "tts" | "video" | "lipsync" | "finalize" | "done";

export const UGC_JOB_STAGES: readonly UgcJobStage[] = [
  "queued",
  "tts",
  "video",
  "lipsync",
  "finalize",
  "done",
] as const;

/**
 * Refund map: dla danego etapu, na którym job padł, jakie składowe NIE zostały
 * jeszcze zużyte i należy je zwrócić użytkownikowi.
 *
 * Reguły:
 *   - 'queued' → fail przed startem: refund WSZYSTKIEGO oprócz overhead.
 *   - 'tts'    → fail przy syntezowaniu audio: refund video + lipsync + whisper.
 *   - 'video'  → fail przy generowaniu wideo: refund lipsync (TTS już zapłaciliśmy do EL).
 *   - 'lipsync' → fail przy lipsyncu: refund 0 (wszystko już zapłacone).
 *   - 'finalize' / 'done' → refund 0.
 */
export function refundableComponents(stage: UgcJobStage): Array<keyof UgcJobCostBreakdown> {
  if (stage === "queued") {
    return ["videoTokens", "ttsTokens", "lipsyncTokens", "whisperTokens", "scriptLlmTokens"];
  }
  if (stage === "tts") {
    return ["videoTokens", "lipsyncTokens", "whisperTokens"];
  }
  if (stage === "video") {
    return ["lipsyncTokens"];
  }
  return [];
}

export function sumBreakdown(b: UgcJobCostBreakdown): number {
  return (
    b.videoTokens +
    b.ttsTokens +
    b.lipsyncTokens +
    b.whisperTokens +
    b.scriptLlmTokens +
    b.llmOverheadTokens
  );
}
